import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { PageLoader } from '../ui/PageLoader';

// Preserve legacy accounts' existing free/read-only surfaces when they have
// no active entitlement. Auth creation time is server-owned, unlike user metadata.
const CARD_REQUIRED_SIGNUP_CUTOFF = Date.parse('2026-09-17T20:09:50Z');

/** New accounts have an incomplete bootstrap row until Stripe confirms checkout. */
export function TrialCheckoutGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore(s => s.user);
  const subscription = useAuthStore(s => s.subscription);
  const refreshSubscription = useAuthStore(s => s.refreshSubscription);
  const location = useLocation();
  const [waiting, setWaiting] = useState(true);
  const checkoutReturn = new URLSearchParams(location.search).get('checkout') === 'success';
  const newSignup = !user || Date.parse(user.created_at) >= CARD_REQUIRED_SIGNUP_CUTOFF;
  const needsCheckout = subscription?.status === 'incomplete' || (!subscription && newSignup);

  useEffect(() => {
    if (!needsCheckout || !checkoutReturn) return;
    // A redirect is not proof of payment. Wait for the webhook-backed row.
    const poll = window.setInterval(() => { void refreshSubscription(); }, 2000);
    const timeout = window.setTimeout(() => {
      window.clearInterval(poll);
      setWaiting(false);
    }, 30000);
    return () => { window.clearInterval(poll); window.clearTimeout(timeout); };
  }, [needsCheckout, checkoutReturn, refreshSubscription]);

  if (!needsCheckout || location.pathname === '/parent' || location.pathname === '/settings') return <>{children}</>;
  if (!checkoutReturn) return <Navigate to="/pricing" replace />;
  if (waiting) return <PageLoader />;
  return <div className="p-8 text-center">
    <h1 className="text-xl font-bold">Your trial is still being confirmed</h1>
    <p className="my-4">Refresh in a moment. If you completed checkout, do not start another subscription.</p>
    <button className="underline mr-4" onClick={() => window.location.reload()}>Refresh</button>
    <Link className="underline" to="/pricing">Back to plans</Link>
  </div>;
}
